// SignUpPage.tsx
import { useMemo, useState, useCallback } from "react";
import {
 Grid,
 Box,
 Alert,
 Checkbox,
 FormControlLabel,
 Tooltip,
 Typography,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuthentication } from "hooks";
import { authenticationService, uploadProfileImage } from "services";
import {
 BasePage,
 LinkButton,
 LoadingButton,
 EmailInput,
 PasswordInput,
 TextInput,
 GenderInput,
 BirthdayInput,
 AvatarPicker,
} from "components";
import { SIGN_UP_ERROR_MESSAGES } from "constants";
import { formatBirthday } from "utilities";

export function SignUpPage() {
 const { isButtonLoading, signUp } = useAuthentication();
 const location = useLocation();

 const urlParams = new URLSearchParams(location.search);
 const returnUrl = location.state?.returnUrl || urlParams.get("returnUrl");

 const [values, setValues] = useState({
  firstName: "",
  lastName: "",
  birthday: null,
  gender: "",
  email: "",
  password: "",
  confirmPassword: "",
  agree: false,
 });

 const [validation, setValidation] = useState({
  firstName: false,
  lastName: false,
  birthday: false,
  email: false,
  password: false,
  confirmPassword: false,
 });

 const [submitError, setSubmitError] = useState("");
 const [submitSuccess, setSubmitSuccess] = useState("");
 const [submitted, setSubmitted] = useState(false);
 const [avatarFile, setAvatarFile] = useState(null);
 const [avatarPreview, setAvatarPreview] = useState(null);

 // ---- Stable setters / handlers ----

 // Change handlers can be ephemeral, but we’ll keep a generic one:
 const handleFieldChange = useCallback(
  (field) => (value) => {
   if (submitError) setSubmitError("");
   setValues((v) => ({ ...v, [field]: value }));
  },
  [submitError],
 );

 // IMPORTANT: stable per-field validation updaters to avoid re-create every render
 const setValidationField = useCallback((field, ok) => {
  setValidation((v) => (v[field] === ok ? v : { ...v, [field]: ok }));
 }, []);

 const validators = useMemo(
  () => ({
   firstName: (ok) => setValidationField("firstName", ok),
   lastName: (ok) => setValidationField("lastName", ok),
   birthday: (ok) => setValidationField("birthday", ok),
   email: (ok) => setValidationField("email", ok),
   password: (ok) => setValidationField("password", ok),
   confirmPassword: (ok) => setValidationField("confirmPassword", ok),
  }),
  [setValidationField],
 );

 const handleConfirmPasswordChange = useCallback(
  (confirmPassword) => {
   if (submitError) setSubmitError("");
   setValues((v) => ({ ...v, confirmPassword }));
  },
  [submitError],
 );

 const handleAgreeChange = useCallback(
  (ev) => {
   if (submitError) setSubmitError("");
   setValues((v) => ({ ...v, agree: ev.target.checked }));
  },
  [submitError],
 );

 const isFormValid =
  validation.firstName &&
  validation.lastName &&
  validation.birthday &&
  validation.email &&
  validation.password &&
  validation.confirmPassword &&
  values.agree;

 const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitted(true);
  setSubmitError("");
  setSubmitSuccess("");

  if (!isFormValid) return;

  const email = values.email.trim();
  const password = values.password.trim();
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  const birthdayStr = formatBirthday(values.birthday);

  try {
   const user = await signUp(email, password);
   setValues((v) => ({ ...v, password: "", confirmPassword: "" }));

   // Upload profile photo first if one was selected, then save profile
   let photoUrl = null;
   if (avatarFile) {
    try {
     photoUrl = await uploadProfileImage(user.uid, avatarFile);
    } catch (photoErr) {
     console.error(
      "Profile photo upload failed, continuing without it:",
      photoErr,
     );
    }
   }

   await authenticationService.saveUserProfile(user, {
    firstName,
    lastName,
    birthday: birthdayStr,
    gender: values.gender || null,
    ...(photoUrl ? { photoUrl } : {}),
   });

   setSubmitSuccess("Account created! You can now sign in.");
  } catch (err) {
   setValues((v) => ({ ...v, password: "", confirmPassword: "" }));
   console.error("Sign up error:", err);
   const message =
    (err && err.code && SIGN_UP_ERROR_MESSAGES[err.code]) ||
    "An unexpected error occurred. Please try again.";
   setSubmitError(message);
  }
 };

 return (
  <BasePage title="Sign up">
   <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box component="form" noValidate onSubmit={handleSubmit}>
     <Grid container spacing={2}>
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
       <AvatarPicker
        src={avatarPreview}
        initials={
         values.firstName || values.lastName
          ? `${values.firstName?.charAt(0) || ""}${values.lastName?.charAt(0) || ""}`.toUpperCase()
          : undefined
        }
        size={80}
        onChange={(file) => {
         setAvatarFile(file);
         const url = URL.createObjectURL(file);
         setAvatarPreview(url);
        }}
       />
      </Grid>

      <Grid size={12}>
       <TextInput
        label="First name"
        value={values.firstName}
        onChange={handleFieldChange("firstName")}
        onValidationChange={validators.firstName}
        required
        autoComplete="given-name"
       />
      </Grid>

      <Grid size={12}>
       <TextInput
        label="Last name"
        value={values.lastName}
        onChange={handleFieldChange("lastName")}
        onValidationChange={validators.lastName}
        required
        autoComplete="family-name"
       />
      </Grid>

      <Grid size={12}>
       <BirthdayInput
        value={values.birthday}
        onChange={handleFieldChange("birthday")}
        onValidationChange={validators.birthday}
        required
        minAge={18}
       />
      </Grid>

      <Grid size={12}>
       <GenderInput
        value={values.gender}
        onChange={handleFieldChange("gender")}
       />
      </Grid>

      <Grid size={12}>
       <EmailInput
        value={values.email}
        onChange={handleFieldChange("email")}
        onValidationChange={validators.email}
       />
      </Grid>

      <Grid size={12}>
       <PasswordInput
        value={values.password}
        onChange={handleFieldChange("password")}
        onValidationChange={validators.password}
        label="Password"
        autoComplete="new-password"
        enforceMinLength={true}
       />
      </Grid>

      <Grid size={12}>
       <PasswordInput
        value={values.confirmPassword}
        onChange={handleConfirmPasswordChange}
        onValidationChange={validators.confirmPassword}
        label="Confirm password"
        autoComplete="new-password"
        enforceMinLength={false}
        validate={(val) => {
         const c = val.trim();
         if (!c) return "Please confirm your password.";
         if (c !== values.password.trim()) return "Passwords do not match.";
         return "";
        }}
       />
      </Grid>

      <Grid size={12}>
       <Tooltip title="You must accept to create your account.">
        <FormControlLabel
         control={
          <Checkbox checked={values.agree} onChange={handleAgreeChange} />
         }
         label={
          <span>
           I understand the{" "}
           <LinkButton to="/terms">Terms &amp; Conditions</LinkButton>
          </span>
         }
        />
       </Tooltip>
       {submitted && !values.agree && (
        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
         You must accept the Terms &amp; Conditions.
        </Typography>
       )}
      </Grid>

      {submitError && (
       <Grid size={12}>
        <Alert icon={<Clear fontSize="inherit" />} severity="error">
         {submitError}
        </Alert>
       </Grid>
      )}

      {submitSuccess && (
       <Grid size={12}>
        <Alert severity="success">
         {submitSuccess}{" "}
         <LinkButton
          to="/sign-in"
          state={returnUrl ? { returnUrl } : undefined}
         >
          Go to sign in
         </LinkButton>
        </Alert>
       </Grid>
      )}

      <Grid size={12}>
       <LoadingButton
        type="submit"
        loading={isButtonLoading}
        disabled={!isFormValid}
       >
        Sign up
       </LoadingButton>
      </Grid>

      <Grid size={12} className="form-actions">
       <LinkButton to="/sign-in" state={returnUrl ? { returnUrl } : undefined}>
        Back to sign in
       </LinkButton>
      </Grid>
     </Grid>
    </Box>
   </LocalizationProvider>
  </BasePage>
 );
}
